import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';
import { localToday } from '../../utils/date';

// "Logged today" (Log page, 2026-10 restyle): one row per entry, in time
// order. Time · what it was · where it came from. Automatic = a device wrote
// it (green dot); You said = we read it out of your words (the quote sits
// under it); One tap = a routine.

const timeOf = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'pm' : 'am'}`;
};

const words = (s) => String(s || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const fmtNum = (v) => (Number.isInteger(v) ? v.toLocaleString() : Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 }));

// "Slept 7h 10m" · "Weight 81.6 kg" · "Grilled chicken breast, about 165 calories"
const titleOf = (e) => {
  const d = e.data || {};
  if (e.entry_type === 'sleep' || e.category_slug === 'sleep') {
    const mins = e.duration_minutes ?? (e.value_unit === 'min' ? e.value : null);
    if (mins != null) return `Slept ${Math.floor(mins / 60)}h ${Math.round(mins % 60)}m`;
  }
  const name = words(d.food_name || e.entry_type || e.category_slug);
  const bits = [];
  if (e.value != null && e.value_unit) bits.push(`${fmtNum(e.value)} ${e.value_unit}`);
  else if (e.value != null) bits.push(fmtNum(e.value));
  const cal = e.calories ?? d.calories;
  if (cal) bits.push(`about ${fmtNum(cal)} calories`);
  if (e.duration_minutes && !bits.length) bits.push(`${e.duration_minutes} minutes`);
  return bits.length ? `${name}, ${bits.join(', ')}` : name;
};

const kindOf = (e) => {
  if (e.source === 'device' || e.source === 'fitbit' || e.source === 'withings' || e.source === 'oura') return { label: 'Automatic', auto: true };
  if (e.source === 'template' || e.source === 'routine' || e.source === 'checklist') return { label: 'One tap' };
  if (e.source === 'ai_logged' || e.raw_input) return { label: 'You said' };
  return { label: 'Logged' };
};

const subOf = (e, kind) => {
  if (kind.auto) return 'From your device';
  if (e.raw_input) {
    const q = e.raw_input.trim();
    return `“${q.length > 90 ? `${q.slice(0, 88)}…` : q}”`;
  }
  return e.notes || null;
};

// One timeline row. Shared with the Data page's history.
export const EntryRow = ({ entry: e }) => {
  const kind = kindOf(e);
  const sub = subOf(e, kind);
  return (
    <div className="grid grid-cols-[72px_1fr] items-center gap-x-3.5 gap-y-1 px-5 py-3.5 sm:grid-cols-[84px_1fr_auto]">
      <span className="text-sm tabular-nums text-fg-3">{timeOf(e.entry_time)}</span>
      <span className="min-w-0">
        <span className="block text-[1.05rem] font-medium">{titleOf(e)}</span>
        {sub && <span className="mt-0.5 block text-sm text-fg-3">{sub}</span>}
      </span>
      <span className="col-start-2 inline-flex items-center gap-2 text-sm text-fg-3 sm:col-start-auto">
        <i className={`inline-block h-2 w-2 rounded-full ${kind.auto ? 'bg-good-strong dark:bg-good' : 'bg-fg-3'}`} />
        {kind.label}
      </span>
    </div>
  );
};

const LoggedToday = ({ date, refreshKey = 0 }) => {
  const day = date || localToday();
  const isToday = day === localToday();
  const [entries, setEntries] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/health/entries', { params: { start_date: day, end_date: day, limit: 100 } });
      setEntries(res.data || []);
    } catch { setEntries([]); }
  }, [day]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const rows = (entries || []).slice().sort((a, b) => {
    if (!a.entry_time && !b.entry_time) return (a.created_at || '').localeCompare(b.created_at || '');
    if (!a.entry_time) return 1;
    if (!b.entry_time) return -1;
    return a.entry_time.localeCompare(b.entry_time);
  });

  return (
    <section className="mt-10">
      <div className="mb-3.5 flex flex-wrap items-baseline justify-between gap-4">
        <h2 className="text-[1.2rem] font-medium tracking-[-0.015em]">{isToday ? 'Logged today' : 'Logged that day'}</h2>
        <Link to="/data" className="text-sm font-medium text-fg-2 hover:text-fg">See all days →</Link>
      </div>
      {entries === null ? (
        <div className="h-24 animate-pulse rounded-xl border border-line" />
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-line px-5 py-5 text-fg-3">
          {isToday ? 'Nothing logged yet today. Say something above, or tap a routine.' : 'Nothing was logged that day.'}
        </div>
      ) : (
        <div className="divide-y divide-line rounded-xl border border-line">
          {rows.map((e) => <EntryRow key={e.id} entry={e} />)}
        </div>
      )}
    </section>
  );
};

export default LoggedToday;
